import { useState, useEffect } from 'react';

// Currency code → symbol, prefix & locale mapping
export const CURRENCY_MAP = {
  INR: { symbol: '₹', prefix: 'Rs', locale: 'en-IN', code: 'INR' },
  AED: { symbol: 'AED', prefix: 'AED', locale: 'en-AE', code: 'AED' },
  USD: { symbol: '$', prefix: '$', locale: 'en-US', code: 'USD' },
  EUR: { symbol: '€', prefix: '€', locale: 'de-DE', code: 'EUR' },
  GBP: { symbol: '£', prefix: '£', locale: 'en-GB', code: 'GBP' },
  SAR: { symbol: 'SAR', prefix: 'SAR', locale: 'ar-SA', code: 'SAR' },
  QAR: { symbol: 'QAR', prefix: 'QAR', locale: 'ar-QA', code: 'QAR' },
  BHD: { symbol: 'BHD', prefix: 'BHD', locale: 'ar-BH', code: 'BHD' },
  OMR: { symbol: 'OMR', prefix: 'OMR', locale: 'ar-OM', code: 'OMR' },
  KWD: { symbol: 'KWD', prefix: 'KWD', locale: 'ar-KW', code: 'KWD' }
};

/**
 * Get active currency code from localStorage or adminAuth.
 * Defaults to 'INR'.
 */
export const getActiveCurrency = () => {
  try {
    const stored = localStorage.getItem('activeCurrency');
    if (stored && stored.trim() && stored !== 'USD') return stored.trim().toUpperCase();

    const adminAuthStr = localStorage.getItem('adminAuth');
    if (adminAuthStr) {
      const auth = JSON.parse(adminAuthStr);
      if (auth.currency) return String(auth.currency).toUpperCase();
      if (auth.hotel?.currency) return String(auth.hotel.currency).toUpperCase();
      if (auth.tenant?.currency) return String(auth.tenant.currency).toUpperCase();

      const country = (auth.tenant?.country || auth.hotel?.country || '').toLowerCase();
      const city = (auth.tenant?.city || auth.hotel?.city || '').toLowerCase();
      if (country.includes('uae') || country.includes('united arab emirates') || city.includes('dubai') || country.includes('emirates')) {
        return 'AED';
      }
      if (country.includes('india')) {
        return 'INR';
      }

      if (auth.tenants && Array.isArray(auth.tenants)) {
        const activeT = auth.tenants.find(t => String(t.tenantId || t.id || t._id) === String(auth.tenantId));
        if (activeT) {
          if (activeT.currency) return String(activeT.currency).toUpperCase();
          const tCountry = (activeT.country || '').toLowerCase();
          const tCity = (activeT.city || '').toLowerCase();
          if (tCountry.includes('uae') || tCountry.includes('united arab emirates') || tCity.includes('dubai') || tCountry.includes('emirates')) {
            return 'AED';
          }
          if (tCountry.includes('india')) {
            return 'INR';
          }
        }
      }
    }

    if (stored && stored.trim()) return stored.trim().toUpperCase();
  } catch (_) {}
  return 'INR';
};

/**
 * Update active currency in storage and broadcast to components.
 */
export const setActiveCurrency = (currencyCode) => {
  if (!currencyCode) return;
  const upper = String(currencyCode).trim().toUpperCase();
  try {
    const current = localStorage.getItem('activeCurrency');
    if (current !== upper) {
      localStorage.setItem('activeCurrency', upper);
      window.dispatchEvent(new CustomEvent('currencyChanged', { detail: upper }));
    }
  } catch (_) {}
};

/**
 * Get currency details (symbol, prefix, locale).
 */
export const getCurrencyInfo = (code) => {
  const c = (code || getActiveCurrency() || 'INR').toUpperCase();
  return CURRENCY_MAP[c] || { symbol: c, prefix: c, locale: 'en-US', code: c };
};

/**
 * Get display symbol for currency (e.g. 'AED' or '₹').
 */
export const getCurrencySymbol = (code) => {
  return getCurrencyInfo(code).symbol;
};

/**
 * Format a numeric amount to localized currency string.
 * Example:
 *   formatCurrency(1500, 'INR') => '₹1,500.00'
 *   formatCurrency(1500, 'AED') => 'AED 1,500.00'
 */
export const formatCurrency = (amount, code) => {
  const val = Number(amount) || 0;
  const info = getCurrencyInfo(code);
  try {
    return new Intl.NumberFormat(info.locale, {
      style: 'currency',
      currency: info.code,
      currencyDisplay: info.code === 'AED' ? 'code' : 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(val);
  } catch (_) {
    try {
      return new Intl.NumberFormat(info.locale, {
        style: 'currency',
        currency: info.code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val);
    } catch (e) {
      return `${info.symbol} ${val.toLocaleString(info.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }
};

/**
 * Format a numeric amount with locale grouping and 2 decimal places.
 */
export const formatAmount = (amount, code) => {
  const val = Number(amount) || 0;
  const info = getCurrencyInfo(code);
  return val.toLocaleString(info.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * React hook for consuming and reacting to currency changes.
 */
export const useCurrency = (explicitCode = null) => {
  const [currency, setCurrency] = useState(() => explicitCode || getActiveCurrency());

  useEffect(() => {
    if (explicitCode) {
      setCurrency(explicitCode);
      return;
    }

    const handleCurrencyChange = (e) => {
      if (e?.detail) setCurrency(e.detail);
      else setCurrency(getActiveCurrency());
    };

    window.addEventListener('currencyChanged', handleCurrencyChange);
    window.addEventListener('storage', handleCurrencyChange);
    return () => {
      window.removeEventListener('currencyChanged', handleCurrencyChange);
      window.removeEventListener('storage', handleCurrencyChange);
    };
  }, [explicitCode]);

  const info = getCurrencyInfo(currency);

  return {
    currency,
    currencySymbol: info.symbol,
    currencyPrefix: info.prefix,
    formatCurrency: (val, overrideCode) => formatCurrency(val, overrideCode || currency),
    formatAmount: (val, overrideCode) => formatAmount(val, overrideCode || currency),
    getCurrencyInfo: (overrideCode) => getCurrencyInfo(overrideCode || currency),
  };
};

export default {
  CURRENCY_MAP,
  getActiveCurrency,
  setActiveCurrency,
  getCurrencyInfo,
  getCurrencySymbol,
  formatCurrency,
  formatAmount,
  useCurrency
};
